import React from "react";
import { useTranslation } from "react-i18next";
import { RoomMatch } from "../../../api/room";
import { movieTitleHandler } from "../../../utils/helpers";
import styles from "./RoomMatchesSidebar.module.scss";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

interface RoomMatchesSidebarProps {
  matches: RoomMatch[];
  isOpen: boolean;
  onClose: () => void;
  onSelectMatch: (matchId: number) => void;
}

const RoomMatchesSidebar: React.FC<RoomMatchesSidebarProps> = ({
  matches,
  isOpen,
  onClose,
  onSelectMatch,
}) => {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <div className={styles.sidebarTitle}>
          <svg width="24" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <span>{t("room_common_choice", "Common Choice")}</span>
        </div>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className={styles.matchList}>
        {matches.map((match) => {
          const detail = match.movie_detail;
          const title = detail
            ? movieTitleHandler(i18n.language, detail) || detail.title
            : match.movie_title;
          const posterPath = detail?.poster_path || match.movie_poster_path;
          const year = detail?.release_date?.slice(0, 4);
          const genres = detail?.genres?.split(",").map((g) => g.trim()).slice(0, 2).join(" ");
          const rating = detail
            ? (detail.imdb_rating?.toFixed(1) || detail.vote_average?.toFixed(1))
            : null;
          const likesCount = detail?.likes_count;

          return (
            <div
              key={match.id}
              className={`${styles.matchCard} ${match.is_selected ? styles.matchCardSelected : ""}`}
              onClick={() => !match.is_selected && onSelectMatch(match.id)}
            >
              <div className={styles.matchPoster}>
                {posterPath ? (
                  <img src={`${IMG_BASE}${posterPath}`} alt={title} />
                ) : (
                  <div className={styles.matchPosterPlaceholder}>{title?.charAt(0)}</div>
                )}
              </div>
              <div className={styles.matchInfo}>
                <div className={styles.matchTitle}>{title}</div>
                <div className={styles.matchMeta}>
                  {year && <span className={styles.matchYear}>{year}</span>}
                </div>
                {genres && <div className={styles.matchGenres}>{genres}</div>}
                <div className={styles.matchStats}>
                  {rating && (
                    <span className={styles.matchRating}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#f5c518">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      {rating}
                    </span>
                  )}
                  {likesCount != null && (
                    <span className={styles.matchLikes}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                      {likesCount < 1000 ? likesCount : `${(likesCount / 1000).toFixed(1)}k`}
                    </span>
                  )}
                </div>
                {match.is_selected && (
                  <span className={styles.selectedBadge}>
                    {t("room_selected", "Selected")}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {matches.length === 0 && (
          <div className={styles.emptyState}>
            {t("room_no_matches_yet", "No matches yet")}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomMatchesSidebar;
